import { useMemo } from 'react';
import { useGT } from 'gt-react';
import type { WorkbookRunMode } from '@erp/shared-types';
import {
  runModeOptions,
  type RunModeBlocker,
  type RunModeOption,
} from '@erp/workbooks';
import type { WorkbookEditor } from './useWorkbookEditor';
import { useDiagnosticText } from './WorksheetDiagnostics';

export type RunModesEditor = Pick<WorkbookEditor, 'definition' | 'plan'> & {
  runs: Pick<WorkbookEditor['runs'], 'data'>;
};
export type RunModes = Record<WorkbookRunMode, RunModeOption>;

/**
 * Which Run modes can start on `targetSheetId` for the edited Definition, unsaved
 * edits included. Advisory: the server re-checks the same rules when a Run starts.
 */
export function useRunModes(
  editor: RunModesEditor,
  targetSheetId: string | null,
): RunModes {
  const { definition, plan } = editor;
  const runs = editor.runs.data;
  return useMemo(
    () =>
      runModeOptions(
        { definition, diagnostics: plan.diagnostics, runs: runs ?? [] },
        targetSheetId,
      ),
    [definition, plan.diagnostics, runs, targetSheetId],
  );
}

/** Labels for each Run mode, and sentences for why one cannot start. */
export function useRunModeText(definition: WorkbookEditor['definition']) {
  const gt = useGT();
  const diagnosticText = useDiagnosticText(definition);
  const itemName = (itemId: string) =>
    [...definition.sources, ...definition.sheets].find(
      (item) => item.id === itemId,
    )?.name ??
    gt('An earlier sheet or data source', {
      context: 'workbook run mode removed item',
    });
  /** A whole-Workbook Run is the toolbar's Run Workbook (or Run on sample), never a Target's. */
  const labels: Record<Exclude<WorkbookRunMode, 'entire'>, string> = {
    'up-to': gt('Run through this sheet', { context: 'workbook run mode' }),
    from: gt('Run from this sheet', { context: 'workbook run mode' }),
    only: gt('Run this sheet only', { context: 'workbook run mode' }),
    sample: gt('Run on sample', { context: 'workbook run mode' }),
  };
  function blockerText(blocker: RunModeBlocker): string {
    switch (blocker.code) {
      case 'no-worksheets':
        return gt('Add a worksheet first.', {
          context: 'workbook run mode blocked',
        });
      case 'no-target':
        return gt('This worksheet no longer exists.', {
          context: 'workbook run mode blocked',
        });
      case 'no-sampling':
        return gt('Choose a sampling key first.', {
          context: 'workbook run mode blocked',
        });
      case 'diagnostic': {
        const { item, part, message } = diagnosticText(blocker.diagnostic);
        return gt('Fix this first: {problem}', {
          context: 'workbook run mode blocked',
          problem: [item, part, message].filter(Boolean).join(' · '),
        });
      }
      case 'no-base-run':
        return gt(
          "Run the workbook first. Running from a sheet reuses an earlier run's inputs.",
          { context: 'workbook run mode blocked' },
        );
      case 'not-in-base-run':
        return gt(
          '{name} has no result in the last run. Run through this sheet first.',
          {
            context: 'workbook run mode blocked',
            name: itemName(blocker.itemId),
          },
        );
      case 'changed-since-base-run':
        return gt(
          '{name} changed since the last run. Run through this sheet first.',
          {
            context: 'workbook run mode blocked',
            name: itemName(blocker.itemId),
          },
        );
    }
  }
  /** What fills a Worksheet's cells that have no Current or Preview Result. */
  function needsRunHint(modes: RunModes): string {
    if (modes.from.allowed)
      return gt('Run from this sheet to fill this in.', {
        context: 'workbook needs run hint',
      });
    if (modes['up-to'].allowed)
      return gt('Run through this sheet to fill this in.', {
        context: 'workbook needs run hint',
      });
    return modes['up-to'].blocker
      ? blockerText(modes['up-to'].blocker)
      : labels['up-to'];
  }
  return { labels, blockerText, needsRunHint };
}
